import { FaroTable } from "@components/common/faro-table/faro-table";
import { FaroTableColumnVisibilityMenu } from "@components/common/faro-table/faro-table-column-visibility-menu";
import { useHandleColumnOrderChange } from "@hooks/use-handle-column-order-change";
import { GridSortModel } from "@mui/x-data-grid-pro";
import { TableType } from "@utils/track-event/track-event-list";
import { useCallback, useEffect } from "react";
import { useSnapshotsTableColumns } from "@pages/project-details/project-snapshots/hooks/use-snapshots-table-columns";
import { SnapshotsSortBy } from "@pages/project-details/project-snapshots/snapshots-table-types";
import {
  SNAPSHOTS_SORT_BY_MAP,
  SNAPSHOTS_TABLE_COLUMN_LABELS,
} from "@pages/project-details/project-snapshots/snapshots-table-constants";
import { getSortByFromGridSortModel } from "@pages/project-details/project-snapshots/snapshots-table-utils";
import { Snapshot } from "@custom-types/base-types";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { SnapshotBulkButtonComponents } from "@components/common/faro-table/faro-table-types";
import { setTableType } from "@store/table/table-slice";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import {
  getOpenSnapshotHref,
  trackOpenSnapshot,
} from "@pages/project-details/project-snapshots/snapshot-utils";
import { defaultProjectLaunchTargetSelector } from "@store/sdb-company/sdb-company-selector";

interface Props {
  /** The snapshots that is shown in the table */
  snapshots: Snapshot[];

  /** Whether the data for showing a table is loading or not */
  isLoading: boolean;

  /** Snapshots sort criteria */
  sortBy: SnapshotsSortBy;

  /** Callback to update the sort criteria */
  setSortBy: (sortBy: SnapshotsSortBy) => void;

  /** List of all the bulk actions and callbacks */
  bulkActionButtons: SnapshotBulkButtonComponents;

  /** Whether to hide the checkbox on each row or not */
  shouldHideCheckbox?: boolean;
}

/** Listing all the snapshots of a project in a table */
export function SnapshotsTable({
  snapshots,
  isLoading,
  sortBy,
  setSortBy,
  bulkActionButtons,
  shouldHideCheckbox = true,
}: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const { trackAsyncEvent } = useTrackEvent();
  const projectLaunchSettings = useAppSelector(
    defaultProjectLaunchTargetSelector
  );

  const columns = useSnapshotsTableColumns();

  const sortModel = [SNAPSHOTS_SORT_BY_MAP[sortBy]];

  // Make sure the correct table type is set in the store.
  useEffect(() => {
    dispatch(setTableType(TableType.snapshots));
  }, [dispatch]);

  /** Callback fired when the sort option changes */
  const handleSortModelChange = useCallback(
    (newModel: GridSortModel) => {
      const newSortBy = getSortByFromGridSortModel(newModel);

      setSortBy(newSortBy);
    },
    [setSortBy]
  );

  const handleRowClick = useCallback(
    async (snapshot: Snapshot) => {
      const openTarget = projectLaunchSettings.defaultOpenProjectTarget;

      await void trackOpenSnapshot({
        openTarget,
        trackAsyncEvent,
        openFrom: "snapshot-table-row",
        accessLevel: snapshot.accessLevel,
        createdAt: snapshot.createdAt,
      });

      const targetHref = getOpenSnapshotHref({
        openTarget,
        snapshotId: snapshot.id,
      });

      window.open(targetHref, "_blank", "noopener,noreferrer");
    },
    [projectLaunchSettings.defaultOpenProjectTarget, trackAsyncEvent]
  );

  /** Get the function to handle column order changes based on the table type */
  const handleColumnOrderChange = useHandleColumnOrderChange(
    TableType.snapshots
  );

  return (
    <FaroTable
      entities={snapshots}
      shouldHideCheckbox={shouldHideCheckbox}
      uniqueIdKey="id"
      isLoading={isLoading}
      columns={columns}
      onRowClick={handleRowClick}
      bulkActionButtons={bulkActionButtons}
      rowHeight={70}
      tableType={TableType.snapshots}
      onSortModelChange={handleSortModelChange}
      sortTrackingSubject="Snapshot"
      sortModel={sortModel}
      filterMenuTrigger={
        <FaroTableColumnVisibilityMenu
          labelsMap={SNAPSHOTS_TABLE_COLUMN_LABELS}
          disabledColumns={["name"]}
          tableType={TableType.snapshots}
        />
      }
      onColumnOrderChange={handleColumnOrderChange}
      sx={{
        "& .MuiDataGrid-cell": {
          cursor: "pointer",
          // Padding should be 0 to align the bottom blue border on edit mode on the very bottom of the cell
          padding: "0",
        },

        "& .MuiDataGrid-columnHeader:hover .drag-handle": {
          display: "flex",
        },

        /** Fill all the header */
        "& .MuiDataGrid-columnHeaderTitleContainerContent": {
          width: "100%",
        },
      }}
    />
  );
}
